/**
 * RadioGroup — Single-select option group where all options are visible at once.
 *
 * RadioGroup Component Guidelines
 * - Use RadioGroup when the user must pick exactly one option from a small set
 *   (2-5 items) and seeing all options at once is helpful. For longer lists or
 *   searchable selection, use Combobox instead.
 * - Always wrap in `<FieldSet>` + `<FieldLegend>` for accessibility — radio groups
 *   require a group label.
 * - Set `invalid` on individual `RadioGroupItem` elements when validation fails,
 *   and pair with `<FieldError>` at the group level to display the message.
 * - Use inside `<Field>` with `<FieldLabel>` for each option when options need
 *   descriptions or secondary text.
 * @see https://base-ui.com/react/components/radio Base UI Radio documentation
 * @see https://ui.shadcn.com/docs/components/radio-group shadcn/ui RadioGroup documentation
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import React from "react";

import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group";
import twMerge from "@replo/design-system/utils/twMerge";
import { CircleIcon } from "lucide-react";

interface RadioGroupProps<ClassName extends string = string>
  extends Omit<RadioGroupPrimitive.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function RadioGroup<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: RadioGroupProps<ClassName>) {
  return (
    <RadioGroupPrimitive
      data-slot="radio-group"
      className={twMerge("grid gap-2 w-full", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface RadioGroupItemProps<ClassName extends string = string>
  extends Omit<RadioPrimitive.Root.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  invalid?: boolean;
}

function RadioGroupItem<ClassName extends string>({
  className,
  UNSAFE_className,
  invalid,
  ...props
}: RadioGroupItemProps<ClassName>) {
  return (
    <RadioPrimitive.Root
      data-slot="radio-group-item"
      aria-invalid={invalid}
      className={twMerge(
        "border-component-border data-checked:border-default text-default focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-rose-border aria-invalid:data-checked:border-rose-border aria-invalid:ring-rose-border flex size-4 rounded-full focus-visible:ring-[3px] aria-invalid:ring-[3px] group/radio-group-item peer relative aspect-square shrink-0 border outline-none after:absolute after:-inset-x-3 after:-inset-y-2 cursor-pointer data-disabled:cursor-not-allowed data-disabled:opacity-50",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      <RadioPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="group-aria-invalid/radio-group-item:text-rose text-default flex size-4 items-center justify-center"
      >
        <CircleIcon className="absolute top-1/2 left-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 fill-current" />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  );
}

export { RadioGroup, RadioGroupItem };
